import React, { useState, useCallback, memo } from 'react'
import {
    Modal,
    Paper,
    Slide,
    Typography,
    IconButton,
    Button,
    useTheme,
    Checkbox,
    Theme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { Container } from '../../../../../../components'
import { ScheduleForm } from './ScheduleForm'
import { ScheduleFormData } from '../../types'
import { Company } from '../../../../../../models'

interface Props {
    open: boolean
    onClose: () => void
    scheduleForm: ScheduleFormData
    onFormChange: (form: ScheduleFormData) => void
    onSubmit: () => void
    companies: Company[]
    mode: 'create' | 'edit'
    onDelete?: () => void
}

export const ScheduleModal: React.FC<Props> = memo(
    ({
        open,
        onClose,
        scheduleForm,
        onFormChange,
        onSubmit,
        companies,
        mode = 'create',
        onDelete,
    }) => {
        const theme = useTheme()
        const [showErrors, setShowErrors] = useState(false)
        const [confirmDelete, setConfirmDelete] = useState(false)

        const handleSubmit = useCallback(() => {
            setShowErrors(true)

            const midnight = new Date()
            midnight.setHours(0, 0, 0, 0)

            if (
                !scheduleForm.name ||
                scheduleForm.companies.length === 0 ||
                !scheduleForm.recurrence_pattern ||
                !scheduleForm.start_date ||
                scheduleForm.start_date < midnight
            ) {
                return
            }

            onSubmit()
        }, [
            scheduleForm.name,
            scheduleForm.companies.length,
            scheduleForm.recurrence_pattern,
            scheduleForm.start_date,
            onSubmit,
        ])

        const handleConfirmDelete = useCallback(() => {
            setConfirmDelete(!confirmDelete)
        }, [confirmDelete])

        return (
            <Modal
                open={open}
                onClose={onClose}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Slide direction="up" in={open}>
                    <Paper style={paperStyles(theme)}>
                        <Container
                            style={{
                                flexDirection: 'column',
                                gap: theme.spacing(2),
                            }}
                        >
                            <Container
                                style={{
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: theme.spacing(2),
                                }}
                            >
                                <Typography variant="h6">
                                    {mode === 'create'
                                        ? 'Schedule Lease Audit'
                                        : 'Edit Schedule'}
                                </Typography>
                                <IconButton onClick={onClose}>
                                    <CloseIcon />
                                </IconButton>
                            </Container>

                            <ScheduleForm
                                scheduleForm={scheduleForm}
                                onFormChange={onFormChange}
                                theme={theme}
                                companies={companies}
                                showErrors={showErrors}
                            />

                            <Container
                                style={{
                                    justifyContent: 'space-between',
                                    gap: theme.spacing(1),
                                    marginTop: theme.spacing(2),
                                }}
                            >
                                <div>
                                    {mode === 'edit' && onDelete && (
                                        <Container
                                            style={{ gap: theme.spacing(1) }}
                                        >
                                            <Checkbox
                                                checked={confirmDelete}
                                                onChange={handleConfirmDelete}
                                            />
                                            <Button
                                                disabled={!confirmDelete}
                                                onClick={() => {
                                                    onDelete?.()
                                                }}
                                                variant="outlined"
                                                color="secondary"
                                                style={{
                                                    textTransform: 'none',
                                                }}
                                            >
                                                Delete Schedule
                                            </Button>
                                        </Container>
                                    )}
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: theme.spacing(1),
                                    }}
                                >
                                    <Button
                                        onClick={onClose}
                                        variant="outlined"
                                        style={{ textTransform: 'none' }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSubmit}
                                        style={{ textTransform: 'none' }}
                                    >
                                        {mode === 'create' ? 'Save' : 'Update'}
                                    </Button>
                                </div>
                            </Container>
                        </Container>
                    </Paper>
                </Slide>
            </Modal>
        )
    },
)

const paperStyles = (theme: Theme) => ({
    padding: theme.spacing(3),
    width: '80%',
})

ScheduleModal.displayName = 'ScheduleModal'
